/**
 * api.ts — API fetch 래퍼
 *
 * 인증 토큰은 httpOnly 쿠키로 관리되므로 클라이언트 코드에서
 * 토큰을 직접 다루지 않는다. credentials: 'include'로 쿠키를 자동 전송.
 *
 * 401 응답 시 /auth/refresh를 호출하여 토큰 갱신을 시도하고,
 * 성공하면 원래 요청을 재시도한다. (쿠키가 서버에서 갱신됨)
 */

const API_BASE = import.meta.env.VITE_API_URL || '';

/** 동시 401 응답 시 refresh 요청을 한 번만 보내기 위한 공유 Promise */
let refreshPromise: Promise<Response> | null = null;

interface FetchOptions extends RequestInit {
  /** true이면 credentials를 포함하지 않음 (공개 API용) */
  skipAuth?: boolean;
}

/**
 * API fetch wrapper — httpOnly 쿠키 기반 인증 + 자동 토큰 갱신
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: FetchOptions = {}
): Promise<{ success: true; data: T } | { success: false; error: string | { code: string; message: string } }> {
  const { skipAuth = false, headers: customHeaders, ...rest } = options;

  const headers = new Headers(customHeaders);
  if (!headers.has('Content-Type') && rest.body && !(rest.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  // httpOnly 쿠키를 자동 전송
  const credentials = skipAuth ? 'omit' as const : 'include' as const;

  let res = await fetch(`${API_BASE}${path}`, { ...rest, headers, credentials });

  // 401이면 쿠키 기반 토큰 갱신 시도 (동시 요청 시 1회만 호출)
  if (res.status === 401 && !skipAuth) {
    if (!refreshPromise) {
      refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
    }
    const refreshRes = await refreshPromise;
    refreshPromise = null;

    if (refreshRes.ok) {
      // 갱신 성공 — 새 쿠키가 설정됨, 원래 요청 재시도
      res = await fetch(`${API_BASE}${path}`, { ...rest, headers, credentials });
    }
  }

  return res.json();
}

/** API 에러 응답에서 메시지 문자열 추출 */
export function getErrorMessage(error: string | { code: string; message: string }, fallback = '오류가 발생했습니다'): string {
  if (typeof error === 'string') return error || fallback
  return error.message || fallback
}
